import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { RootState } from '../../redux/store';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faToolbox, faChevronDown, faPen } from '@fortawesome/free-solid-svg-icons';
import LoadingCenter from '../util/LoadingCenter';
import EmptyState from '../util/EmptyState';
import MobileSearchFilter from './MobileSearchFilter';
import MobileSelect from './MobileSelect';

interface Props { access: any; }

const asArray = (d: any) => (Array.isArray(d) ? d : d?.data ?? []);
const fmtDate = (d: any) => (d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '');

interface EditForm {
  manufacturer: string; model: string;
  statusId: string; locationId: string; categoryEquipmentId: string; responsibleUserId: string;
  pat_test: boolean; rental_opportunity: boolean; capitalised: boolean;
}
const emptyEdit: EditForm = { manufacturer: '', model: '', statusId: '', locationId: '', categoryEquipmentId: '', responsibleUserId: '', pat_test: false, rental_opportunity: false, capitalised: false };

const MobileEquipment: React.FC<Props> = ({ access }) => {
  const { t } = useTranslation();
  const auth = useSelector((state: RootState) => state.auth.value);
  const { enqueueSnackbar } = useSnackbar();
  const canEdit = access?.equipment_edit === true;

  const [items, setItems] = useState<any[]>([]);
  const [statuses, setStatuses] = useState<any[]>([]);
  const [locations, setLocations] = useState<any[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [flt, setFlt] = useState<Record<string, string[]>>({});
  const setGroup = (key: string) => (next: string[]) => setFlt((p) => ({ ...p, [key]: next }));
  const [detail, setDetail] = useState<any | null>(null);
  const [editing, setEditing] = useState(false);
  const [edit, setEdit] = useState<EditForm>(emptyEdit);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [eq, st, loc, cat, usr] = await Promise.allSettled([
        axios.get('equipment'), axios.get('status'),
        axios.get('location'), axios.get('category-equipment'), axios.get('users'),
      ]);
      if (eq.status === 'fulfilled') setItems(asArray(eq.value.data));
      if (st.status === 'fulfilled') setStatuses(asArray(st.value.data).filter((s: any) => s.active));
      if (loc.status === 'fulfilled') setLocations(asArray(loc.value.data));
      if (cat.status === 'fulfilled') setCategories(asArray(cat.value.data));
      if (usr.status === 'fulfilled') setUsers(asArray(usr.value.data));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { if (auth) load(); }, [auth, load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const has = (key: string) => (flt[key]?.length ?? 0) > 0;
    const match = (key: string, v: string) => !has(key) || flt[key].includes(v);
    return items.filter((e) => {
      if (!match('status', String(e.statusId))) return false;
      if (!match('category', String(e.categoryEquipmentId))) return false;
      if (!match('location', String(e.locationId))) return false;
      if (!match('responsible', String(e.responsibleUserId))) return false;
      if (!match('pat', String(!!e.pat_test))) return false;
      if (!match('rental', String(!!e.rental_opportunity))) return false;
      if (!match('capitalised', String(!!e.capitalised))) return false;
      if (!q) return true;
      return [e.item_name, e.manufacturer, e.model, e.serial_number, e.inventory_id, e.notes,
        e.categoryEquipment?.name, e.location?.name, e.responsibleUser?.first_name, e.responsibleUser?.last_name]
        .filter(Boolean).join(' ').toLowerCase().includes(q);
    });
  }, [items, search, flt]);

  const filterGroups = useMemo(() => {
    const yesNo = (extract: (e: any) => boolean) => [
      { value: 'true', label: t('Yes') as string, count: items.filter(extract).length },
      { value: 'false', label: t('No') as string, count: items.filter((e) => !extract(e)).length },
    ];
    return [
      {
        key: 'status', label: t('Status') as string, selected: flt.status ?? [], onChange: setGroup('status'),
        options: statuses.map((s) => ({ value: String(s.id), label: s.name, count: items.filter((e) => e.statusId === s.id).length })),
      },
      {
        key: 'category', label: t('Category') as string, selected: flt.category ?? [], onChange: setGroup('category'),
        options: categories.map((c) => ({ value: String(c.id), label: c.name, count: items.filter((e) => e.categoryEquipmentId === c.id).length })),
      },
      {
        key: 'location', label: t('Location') as string, selected: flt.location ?? [], onChange: setGroup('location'),
        options: locations.map((l) => ({ value: String(l.id), label: l.name, count: items.filter((e) => e.locationId === l.id).length })),
      },
      {
        key: 'responsible', label: t('Responsive') as string, selected: flt.responsible ?? [], onChange: setGroup('responsible'),
        options: users.map((u) => ({ value: String(u.id), label: `${u.first_name ?? ''} ${u.last_name ?? ''}`.trim(), count: items.filter((e) => e.responsibleUserId === u.id).length })),
      },
      { key: 'pat', label: t('PATtest') as string, selected: flt.pat ?? [], onChange: setGroup('pat'), options: yesNo((e) => !!e.pat_test) },
      { key: 'rental', label: t('Rental') as string, selected: flt.rental ?? [], onChange: setGroup('rental'), options: yesNo((e) => !!e.rental_opportunity) },
      { key: 'capitalised', label: t('Capitalised') as string, selected: flt.capitalised ?? [], onChange: setGroup('capitalised'), options: yesNo((e) => !!e.capitalised) },
    ];
  }, [t, items, statuses, categories, locations, users, flt]);

  const openDetail = (e: any) => {
    setDetail(e);
    setEditing(false);
    setEdit({
      manufacturer: e.manufacturer ?? '',
      model: e.model ?? '',
      statusId: e.statusId != null ? String(e.statusId) : '',
      locationId: e.locationId != null ? String(e.locationId) : '',
      categoryEquipmentId: e.categoryEquipmentId != null ? String(e.categoryEquipmentId) : '',
      responsibleUserId: e.responsibleUserId != null ? String(e.responsibleUserId) : '',
      pat_test: !!e.pat_test, rental_opportunity: !!e.rental_opportunity, capitalised: !!e.capitalised,
    });
  };

  const save = async () => {
    if (!detail) return;
    setSaving(true);
    try {
      await axios.put(`equipment/${detail.id}`, {
        manufacturer: edit.manufacturer.trim() || null,
        model: edit.model.trim() || null,
        statusId: edit.statusId ? Number(edit.statusId) : null,
        locationId: edit.locationId ? Number(edit.locationId) : null,
        categoryEquipmentId: edit.categoryEquipmentId ? Number(edit.categoryEquipmentId) : null,
        responsibleUserId: edit.responsibleUserId ? Number(edit.responsibleUserId) : null,
        pat_test: edit.pat_test,
        rental_opportunity: edit.rental_opportunity,
        capitalised: edit.capitalised,
      });
      const { data } = await axios.get('equipment');
      const arr = asArray(data);
      setItems(arr);
      setDetail(arr.find((x: any) => x.id === detail.id) || null);
      setEditing(false);
      enqueueSnackbar(t('Saved') as string, { variant: 'success' });
    } catch {
      enqueueSnackbar(t('SaveError') as string, { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <LoadingCenter />;

  const statusBadge = (st: any) => st && (
    <span className="sbadge" style={{ background: st.color ? `${st.color}22` : 'rgba(100,116,139,.12)', color: st.color || '#64748b' }}>{st.name}</span>
  );

  return (
    <div className="flex flex-col gap-[10px]">
      <MobileSearchFilter
        value={search}
        onChange={setSearch}
        groups={filterGroups}
      />

      {filtered.length === 0 ? (
        <EmptyState icon="🧰" message={t('NoEquipment') as string} />
      ) : (
        <div className="m-list">
          {filtered.map((e) => {
            const catColor = e.categoryEquipment?.color || '#f59e0b';
            const open = detail?.id === e.id;
            return (
              <div key={e.id} className={`m-eq-wrap${open ? ' open' : ''}`}>
                <button type="button" className="m-li" onClick={() => (open ? setDetail(null) : openDetail(e))}>
                  <span className="m-li-ic" style={{ background: `${catColor}22`, color: catColor }}><FontAwesomeIcon icon={faToolbox} /></span>
                  <span className="m-li-main">
                    <span className="m-li-title">{e.item_name || '—'}</span>
                    <span className="m-li-sub">{[e.categoryEquipment?.name, e.location?.name].filter(Boolean).join(' · ') || e.serial_number || ''}</span>
                  </span>
                  {statusBadge(e.status)}
                  <FontAwesomeIcon icon={faChevronDown} className={`m-ts-chev${open ? ' open' : ''}`} />
                </button>

                {open && (
                  <div className="m-eq-acc">
                    {canEdit && editing ? (
                      <>
                        <div className="m-ts-field"><label>{t('Manufacturer')}</label>
                          <input className="m-ts-input" value={edit.manufacturer} onChange={(ev) => setEdit({ ...edit, manufacturer: ev.target.value })} placeholder={t('Manufacturer') as string} />
                        </div>
                        <div className="m-ts-field"><label>{t('Model')}</label>
                          <input className="m-ts-input" value={edit.model} onChange={(ev) => setEdit({ ...edit, model: ev.target.value })} placeholder={t('Model') as string} />
                        </div>
                        {[
                          [t('SerialNumber'), e.serial_number],
                          [t('Warranty'), fmtDate(e.warranty)],
                        ].filter(([, v]) => v).map(([label, value], i) => (
                          <div key={i} className="m-kv"><span className="m-kv-lbl">{label}</span><span className="m-kv-val">{value}</span></div>
                        ))}
                        <div className="m-ts-field"><label>{t('Status')}</label>
                          <MobileSelect
                            inline
                            title={t('Status') as string}
                            value={edit.statusId}
                            onChange={(v) => setEdit({ ...edit, statusId: v })}
                            options={[{ value: '', label: '—' }, ...statuses.map((s) => ({ value: String(s.id), label: s.name }))]}
                          />
                        </div>
                        <div className="m-ts-field"><label>{t('Location')}</label>
                          <MobileSelect
                            inline
                            title={t('Location') as string}
                            value={edit.locationId}
                            onChange={(v) => setEdit({ ...edit, locationId: v })}
                            options={[{ value: '', label: '—' }, ...locations.map((l) => ({ value: String(l.id), label: l.name }))]}
                          />
                        </div>
                        <div className="m-ts-field"><label>{t('Category')}</label>
                          <MobileSelect
                            inline
                            title={t('Category') as string}
                            value={edit.categoryEquipmentId}
                            onChange={(v) => setEdit({ ...edit, categoryEquipmentId: v })}
                            options={[{ value: '', label: '—' }, ...categories.map((c) => ({ value: String(c.id), label: c.name }))]}
                          />
                        </div>
                        <div className="m-ts-field"><label>{t('Responsive')}</label>
                          <MobileSelect
                            inline
                            title={t('Responsive') as string}
                            value={edit.responsibleUserId}
                            onChange={(v) => setEdit({ ...edit, responsibleUserId: v })}
                            options={[{ value: '', label: '—' }, ...users.map((u) => ({ value: String(u.id), label: `${u.first_name ?? ''} ${u.last_name ?? ''}`.trim() }))]}
                          />
                        </div>
                        <div className="m-ts-two">
                          <div className="m-ts-field"><label>{t('PATtest')}</label>
                            <MobileSelect
                              inline
                              title={t('PATtest') as string}
                              value={edit.pat_test ? 'true' : 'false'}
                              onChange={(v) => setEdit({ ...edit, pat_test: v === 'true' })}
                              options={[{ value: 'false', label: t('No') as string }, { value: 'true', label: t('Yes') as string }]}
                            />
                          </div>
                          <div className="m-ts-field"><label>{t('Rental')}</label>
                            <MobileSelect
                              inline
                              title={t('Rental') as string}
                              value={edit.rental_opportunity ? 'true' : 'false'}
                              onChange={(v) => setEdit({ ...edit, rental_opportunity: v === 'true' })}
                              options={[{ value: 'false', label: t('No') as string }, { value: 'true', label: t('Yes') as string }]}
                            />
                          </div>
                        </div>
                        <div className="m-ts-field"><label>{t('Capitalised')}</label>
                          <MobileSelect
                            inline
                            title={t('Capitalised') as string}
                            value={edit.capitalised ? 'true' : 'false'}
                            onChange={(v) => setEdit({ ...edit, capitalised: v === 'true' })}
                            options={[{ value: 'false', label: t('No') as string }, { value: 'true', label: t('Yes') as string }]}
                          />
                        </div>
                        <div className="m-ts-form-actions">
                          <button type="button" className="m-ts-cancel" onClick={() => setEditing(false)}>{t('Cancel')}</button>
                          <button type="button" className="btn-primary" disabled={saving} onClick={save}>{t('Save')}</button>
                        </div>
                      </>
                    ) : (
                      <>
                        {canEdit && (
                          <div className="m-eq-edit-row">
                            <button type="button" className="m-eq-edit-btn" onClick={() => setEditing(true)}>
                              <FontAwesomeIcon icon={faPen} /> {t('Edit')}
                            </button>
                          </div>
                        )}
                        {([
                          [t('Manufacturer'), e.manufacturer],
                          [t('Model'), e.model],
                          [t('SerialNumber'), e.serial_number],
                          [t('Warranty'), fmtDate(e.warranty)],
                          [t('Status'), e.status?.name],
                          [t('Location'), e.location?.name],
                          [t('Category'), e.categoryEquipment?.name],
                          [t('Responsive'), e.responsibleUser ? `${e.responsibleUser.first_name ?? ''} ${e.responsibleUser.last_name ?? ''}`.trim() : ''],
                          [t('PATtest'), e.pat_test ? t('Yes') : t('No')],
                          [t('Rental'), e.rental_opportunity ? t('Yes') : t('No')],
                          [t('Capitalised'), e.capitalised ? t('Yes') : t('No')],
                          [t('Notes'), e.notes],
                        ] as [any, any][]).filter(([, v]) => v).map(([label, value], i) => (
                          <div key={i} className="m-kv"><span className="m-kv-lbl">{label}</span><span className="m-kv-val">{value}</span></div>
                        ))}
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MobileEquipment;
